import type { DeviceMode } from '@vedalib/editor'
import type { ElementSchemaType } from '@vedalib/editor/lib/brand'
import type { ElementsTypes, EditorElement, ElementValue } from '@vedalib/editor/lib/elements'

import type { IFieldConfig } from 'components/formKit'
import type {
  BrandFormEnum,
  ElementFeedbackCss,
  ElementFontCss,
  ElementStyleCss,
  ElementStyleValue,
  FieldsSchema,
} from 'services/Branding/types'
import type { AppMode } from 'services/Store/Project/enums'
import type { ElementState, Block, IBlockMode } from 'services/Store/Project/types'

export interface IElementConfig<
  Type extends ElementsTypes = ElementsTypes,
  Value extends ElementValue = ElementValue,
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
  State extends ElementState = ElementState,
> {
  type: Type
  icon: string
  group: ElementGroup
  label: string
  defaultValue: Value
  styleFields: FieldsSchema<Schema> | null
  Element: FCElementType<Value, Schema, Font, Feedback, State>
  Demo: FCDemoType<Schema, Font, Feedback>[]
  FormValue: FCFormValueType<Value, Schema, Font, Feedback, State> | null
  FormBrand: FCFormBrandType<Value, Schema, Font, Feedback, State> | null
  EditorActions: FCEditorActions<Value, Schema, Font, Feedback, State> | null
}

export type FCElementType<
  Value extends ElementValue = ElementValue,
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
  State extends ElementState = ElementState,
> = React.FC<{
  element: EditorElement<Value, Schema, Font, Feedback>
  mode: IBlockMode
  styles: ElementStyleCss<Schema>
  block?: Block | null
  state?: ElementState<State>
  setState?: (value: ElementState<State>) => void
  onChange?: (value: Value) => void
  waiting?: boolean
  font: ElementFontCss<Font>
  feedback: ElementFeedbackCss<Feedback>
  isV2?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: any
}>

export type FCDemoType<
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
> = React.FC<{
  styles: ElementStyleCss<Schema>
  fonts: ElementFontCss<Font>
  feedback: ElementFeedbackCss<Feedback>
}>

export type FCFormValueType<
  Value extends ElementValue = ElementValue,
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
  State extends ElementState = ElementState,
> = React.FC<{
  name: string
  element: EditorElement<Value, Schema, Font, Feedback>
  mode: IBlockMode
  styles: ElementStyleValue<Schema>
  state?: ElementState<State>
  setState: (value: ElementState<State>) => void
}>

export type FCFormBrandType<
  Value extends ElementValue = ElementValue,
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
  _State extends ElementState = ElementState,
> = React.FC<{
  device: DeviceMode
  form: keyof Font | BrandFormEnum
  setForm: (form: BrandFormEnum | string) => void
  subConfigs?: Partial<IFieldConfig>
  element?: EditorElement<Value, Schema, Font, Feedback>
  edited: {
    style?: Record<keyof Schema, boolean>
    feedback?: Record<keyof Feedback, boolean>
    font?: Record<keyof Font, boolean>
  }
}>

export type FCEditorActions<
  Value extends ElementValue = ElementValue,
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
  State extends ElementState = ElementState,
> = React.FC<{
  element: EditorElement<Value, Schema, Font, Feedback>
  customMode: IBlockMode | null
  mode: IBlockMode
  setAppMode: (value: AppMode) => void
  block: Block
  state?: ElementState<State>
  setState: (value: ElementState<State>) => void
  onChange: (value: Value) => void
}>

export type BrandFormsMapType<
  Value extends ElementValue = ElementValue,
  Schema extends ElementSchemaType = ElementSchemaType,
  Font extends ElementSchemaType = ElementSchemaType,
  Feedback extends ElementSchemaType = ElementSchemaType,
  State extends ElementState = ElementState,
> = {
  [Key in keyof Font]: FCFormBrandType<Value, Schema, Font, Feedback, State>
} & {
  [SubForm in BrandFormEnum]?: FCFormBrandType<Value, Schema, Font, Feedback, State>
}

export type Properties<T> = T extends React.FC<infer P> ? P : never

export enum ElementGroup {
  notUsed = 'notUsed',
  view = 'view',
  interactive = 'interactive',
  test = 'test',
  cover = 'cover',
}
